import { createApi } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { type BaseQueryApi } from '@reduxjs/toolkit/query';
import {
    uploadFiles,
    type ImageUploadResponse,
    type PublishMeta,
    type QueryFnBaseQuery,
    type S3Asset,
} from 'ContentItemEditor';
import {
    type EditorPlaylist,
    type LoadedPlaylist,
    type PlaylistIndexResponse,
    type PlaylistRecord,
    type PlaylistShowResponse,
    type PlaylistUpdateResponse,
} from './PlaylistEditor.types';

const COLLECTION = 'playlists';

/*
    Playlist::LocalePack#after_update refreshes the materialized content views and the derived
    content tables, so a save that touches the pack is slow. The Angular save passed
    `timeout: undefined`, so the 30 second default of the shared base query is not a safe ceiling.

    It goes on the save alone, not on the slice. As a slice default it also covered the load, and a
    hung getPlaylist then held the spinner for two minutes rather than thirty seconds.
*/
const SAVE_TIMEOUT = 120000;

const baseConfig = getBackRoyalApiBaseConfig('playlistEditorApi', {
    convertCasing: false,
});

// The params the Angular directive sent through Playlist.getCachedOrShow. `published: false` asks
// for the working version, which needs the view_unpublished permission.
const SHOW_PARAMS = {
    filters: { published: false, in_users_locale_or_en: null },
    'fields[]': ['ADMIN_FIELDS'],
    get_available_content_family_identifiers: true,
};

// Every playlist a translation could be linked to. The locale filters are off, so the response
// covers every locale, which is what the translation links need. The narrow field list is all a
// LinkableContentItem reads.
const INDEX_PARAMS = {
    filters: { published: false, in_users_locale_or_en: null, archived: false },
    'fields[]': ['id', 'title', 'tag', 'locale', 'locale_pack'],
};

function playlistFromShowResponse(response: PlaylistShowResponse): LoadedPlaylist {
    const record = response?.contents?.playlists?.[0];
    if (!record) throw new Error('Unexpected response shape from the playlist load');

    return {
        record,

        // The server omits this meta key for a playlist that has no locale pack. The Angular
        // directive mapped over it unguarded.
        availableContentFamilyIdentifiers: response.meta?.available_content_family_identifiers || [],
    };
}

export const playlistEditorApi = createApi('playlistEditorApi', {
    ...baseConfig,
    tagTypes: ['Playlist'],
    // oxlint-disable-next-line max-lines-per-function -- one object literal of endpoints
    endpoints: builder => ({
        getPlaylist: builder.query<LoadedPlaylist, string>({
            query: id => ({
                url: `${COLLECTION}/${id}.json`,
                params: SHOW_PARAMS,
            }),
            transformResponse: playlistFromShowResponse,
            providesTags: (_result, _error, id) => [{ type: 'Playlist', id }],

            // Re-entering the editor must not serve a stale updated_at. A stale one makes the next
            // save fail the optimistic lock check with a 406.
            forceRefetch: () => true,
        }),

        // Playlist#index is a GET. Only Lesson::Stream overrides index to a POST.
        getPlaylistsForEditorList: builder.query<EditorPlaylist[], void>({
            query: () => ({ url: `${COLLECTION}.json`, params: INDEX_PARAMS }),
            transformResponse: (response: PlaylistIndexResponse) => response?.contents?.playlists || [],
        }),

        // PUT carries no id in the path. The id travels in record.id.
        updatePlaylist: builder.mutation<Partial<PlaylistRecord>, { record: PlaylistRecord; meta: PublishMeta }>({
            query: ({ record, meta }) => ({
                url: `${COLLECTION}.json`,
                method: 'PUT',
                body: { record, meta },
                timeout: SAVE_TIMEOUT,
            }),
            // An empty fallback would keep the loaded updated_at as the new baseline, and the next
            // save would then 406 and tell the admin someone else saved the playlist, which is
            // false. UPDATE_FIELDS always comes back, so this throw means a real server change.
            transformResponse: (response: PlaylistUpdateResponse) => {
                const savedFields = response?.contents?.playlists?.[0];
                if (!savedFields) throw new Error('Unexpected response shape from the playlist save');
                return savedFields;
            },

            // The response carries UPDATE_FIELDS only, so invalidating the Playlist tag would
            // refetch the whole record and discard edits the user has not saved yet. Fold what was
            // sent, plus what came back, into the cached record instead.
            async onQueryStarted({ record }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        playlistEditorApi.util.updateQueryData('getPlaylist', record.id, draft => {
                            Object.assign(draft.record, record, data);
                        }),
                    );
                } catch {
                    // The caller reports the failure. Leave the cache as it was.
                }
            },

            /*
                The shared base query retries once on a disconnect or a 502/504, and RTK applies
                that to mutations too. A retried save re-runs publish! and then fails the updated_at
                check. The Angular $http path never retried.
            */
            extraOptions: { maxRetries: 0 },
        }),

        destroyPlaylist: builder.mutation<void, string>({
            query: id => ({
                url: `${COLLECTION}/${id}.json`,
                method: 'DELETE',
            }),
            extraOptions: { maxRetries: 0 },
        }),

        // A duplicate creates a new playlist, in a new locale pack or in the same one for a
        // translation. A retry would create a second playlist.
        duplicatePlaylist: builder.mutation<
            LoadedPlaylist,
            {
                title: string;
                locale: string;
                duplicateFromId: string;
                duplicateFromUpdatedAt: number;
                inSameLocalePack: boolean;
            }
        >({
            query: ({ title, locale, duplicateFromId, duplicateFromUpdatedAt, inSameLocalePack }) => ({
                url: `${COLLECTION}.json`,
                method: 'POST',
                body: {
                    record: { title, locale },
                    meta: {
                        in_same_locale_pack: inSameLocalePack,
                        duplicate_from_id: duplicateFromId,
                        duplicate_from_updated_at: duplicateFromUpdatedAt,
                    },
                },
            }),
            transformResponse: playlistFromShowResponse,
            extraOptions: { maxRetries: 0 },
        }),

        uploadPlaylistImages: builder.mutation<S3Asset[], { playlistId: string; files: File[] }>({
            async queryFn({ playlistId, files }, _api: BaseQueryApi, _options, fetchWithBaseQuery: QueryFnBaseQuery) {
                const result = await uploadFiles<ImageUploadResponse>(
                    { url: `${COLLECTION}/${playlistId}/images.json`, key: 'images[]', files },
                    fetchWithBaseQuery,
                );
                if (result.error) return { error: result.error };
                return { data: result.data.contents.images };
            },
            extraOptions: { maxRetries: 0 },
        }),
    }),
});
